/**
 * Saddlebag adapter for ConsentBanner.
 *
 * This file is the ONLY place that imports from saddlebag-tracking-preferences.
 * All CMP-specific logic is encapsulated here.
 *
 * From saddlebag-tracking-preferences:
 * - loadCMPSDK (with onLoad/onError/onShowConsentExperience/onHideConsentExperience callbacks)
 * - updateConsent (accept_all, reject_all, set)
 * - CONSENT_ACTIONS constants
 * - getVendorCount
 */

import {
  loadCMPSDK as saddlebagLoadCMPSDK,
  updateConsent as saddlebagUpdateConsent,
  getVendorCount as saddlebagGetVendorCount,
} from 'saddlebag-tracking-preferences';

// =============================================================================
// Re-exports from saddlebag-tracking-preferences
// =============================================================================

export { CONSENT_ACTIONS } from 'saddlebag-tracking-preferences';

// =============================================================================
// Types derived from saddlebag-tracking-preferences function signatures
// =============================================================================

/**
 * Consent action type, derived from saddlebag-tracking-preferences updateConsent param.
 */
export type ConsentAction = Parameters<typeof saddlebagUpdateConsent>[0];

/**
 * SDK configuration type, derived from saddlebag-tracking-preferences loadCMPSDK param.
 */
type SaddlebagCMPSDKConfig = NonNullable<Parameters<typeof saddlebagLoadCMPSDK>[0]>;

// =============================================================================
// Types
// =============================================================================

/**
 * Configuration for loading the CMP SDK.
 * Maps to saddlebag-tracking-preferences CMPSDKConfig interface.
 *
 * Key callbacks for banner visibility:
 * - onShowConsentExperience: Called when Ketch decides to show the consent banner (NOT preference center)
 * - onHideConsentExperience: Called when Ketch hides the consent banner (NOT preference center)
 *
 * These callbacks are the primary mechanism for controlling custom banner visibility.
 * ConsentBanner uses these to show/hide via React state, avoiding observer-based approaches.
 */
export interface LoadCMPSDKConfig {
  /**
   * Called when Ketch determines consent banner should be shown.
   * Triggered by Ketch's 'willShowExperience' event when the experience type is 'consent'.
   * Ketch UI is suppressed; ConsentBanner renders instead.
   *
   * IMPORTANT: This does NOT fire for Preference Center - only for the initial consent banner.
   */
  onShowConsentExperience: () => void;

  /**
   * Called when Ketch hides the consent banner.
   * Triggered by Ketch's 'hideExperience' event when the experience type is 'consent'.
   *
   * IMPORTANT: This does NOT fire for Preference Center - only for the initial consent banner.
   */
  onHideConsentExperience: () => void;

  /**
   * Called when SDK is ready and initialized.
   * Maps to saddlebag's onLoad callback.
   */
  onReady?: () => void;

  /**
   * Called if SDK fails to load (network error, timeout, etc.).
   * Maps to saddlebag's onError callback.
   */
  onError?: (error: Error) => void;

  /**
   * Timeout in ms before triggering onError.
   * @default 10000 (matches saddlebag default)
   */
  timeout?: number;
}

// =============================================================================
// SDK Loading
// =============================================================================

/**
 * Load the CMP SDK and set up event listeners.
 *
 * This function initializes the Ketch CMP SDK via saddlebag-tracking-preferences
 * and registers callbacks for banner visibility.
 *
 * Key callbacks:
 * - onShowConsentExperience: Fires when Ketch decides to show the consent banner
 * - onHideConsentExperience: Fires when Ketch decides to hide the consent banner
 *
 * IMPORTANT: These callbacks only fire for the consent banner, NOT the preference center.
 * This is the authoritative mechanism for controlling custom banner visibility.
 */
export function loadCMPSDK(config: LoadCMPSDKConfig): void {
  const {
    onError,
    onHideConsentExperience,
    onReady,
    onShowConsentExperience,
    timeout = 10000,
  } = config;

  const sdkConfig: SaddlebagCMPSDKConfig = {
    // Banner visibility callbacks - these are the key integration points
    onShowConsentExperience,
    onHideConsentExperience,

    // SDK lifecycle callbacks
    onLoad: onReady,
    // Saddlebag's onError is () => void — wrap to provide an Error object
    // so callers can rely on the (error: Error) signature.
    onError: onError ? () => onError(new Error('SDK load failed')) : undefined,

    // Configuration
    timeoutMs: timeout,
    enableFallback: true,
  };

  saddlebagLoadCMPSDK(sdkConfig);
}

// =============================================================================
// Consent Actions
// =============================================================================

/**
 * Update user consent via Ketch SDK.
 *
 * @example
 * // Accept all
 * await updateConsent({ action: CONSENT_ACTIONS.ACCEPT_ALL });
 *
 * // Reject all
 * await updateConsent({ action: CONSENT_ACTIONS.REJECT_ALL });
 */
export async function updateConsent(request: ConsentAction): Promise<void> {
  return saddlebagUpdateConsent(request);
}

// =============================================================================
// Vendor Count
// =============================================================================

/**
 * Get the number of vendors/partners from Ketch config.
 *
 * @returns Promise with vendor count, or 0 if unavailable
 */
export function getVendorCount(): Promise<number> {
  return saddlebagGetVendorCount();
}
